import type * as React from "react";
import type { Omittable } from "../../lib/props";
import { Box } from "./box";
import { Spinner } from "./spinner";
import { Text } from "./text";

/** A section's heading inside a list or panel: one quiet line naming the
 *  rows under it, never a heading element. */
export function SectionLabel({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <Text data-slot="section-label" size="xs" variant="faint" weight="medium">
      {children}
    </Text>
  );
}

/** A row that states something instead of offering it — "No members yet",
 *  "You have full access" — on the row rung its neighbours keep. */
export function QuietRow({
  children,
  height,
}: Omittable<{
  children: React.ReactNode;
  /** The row rung, in px, so the line lands where a live row would. */
  height: number;
}>): React.ReactElement {
  return (
    <Box data-slot="quiet-row" height={height} justify="center">
      <Text size="sm" variant="muted">
        {children}
      </Text>
    </Box>
  );
}

/** A row's worth of waiting: one small spinner centred on the rung a live
 *  row would take, for a list that appends as it loads. */
export function LoadingRow({
  height,
}: {
  /** The row rung, in px. */
  height: number;
}): React.ReactElement {
  return (
    <Box align="center" data-slot="loading-row" height={height} justify="center">
      <Spinner size="sm" />
    </Box>
  );
}

/**
 * The scroll space under an opened row or group: a recessed band that runs
 * edge to edge across its host, scrolling on the vertical axis only and
 * capped at a height the host names (so many rows, no more). Rows truncate
 * rather than widen, and the horizontal axis is shut so a stray wide row
 * cannot open one. Its padding puts the rows back on the host's column.
 */
export function ScrollBand({
  children,
  maxHeight,
  onScroll,
}: Omittable<{
  children: React.ReactNode;
  /** The band's cap, in px — a row count times the row rung. */
  maxHeight: number;
  onScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
}>): React.ReactElement {
  return (
    <Box paddingY="2xs">
      <Box
        background="background"
        data-slot="scroll-band"
        maxHeight={maxHeight}
        onScroll={onScroll}
        overflowX="hidden"
        overflowY="auto"
        paddingX="lg"
        paddingY="2xs"
      >
        {children}
      </Box>
    </Box>
  );
}
